import {Component, Input, ViewChild, Injector, OnInit} from '@angular/core';
import {GlobalModalChild, GlobalModalParent, GlobalModalService} from '../../../kanso-common/core/service/globalmodal.service';
import {Authority, Program, Project, ProjectModalData} from '../../housing-core/services/housing-models';
import {CustomAttributes, CustomAttributesValue} from 'src/app/core/service/core-models';
import {ISubLedgerMap} from '../../accounting/service/accounting-models';
import {ControlContainer, NgForm, FormGroup, Validators, FormBuilder} from '@angular/forms';
import {HousingServices} from '../../housing-core/services/housing-services.service';
import {ProjectsService} from 'src/app/shared/services/projects.service';
import {ProjectModel, ProjectOverride} from 'src/app/shared/services/project.model';
import lodash from 'lodash';

@Component({
  selector: 'project-setup',
  templateUrl: './project-setup.component.html',
  styleUrls: ['./project-setup.component.scss'],
  providers: [{provide: ControlContainer, useExisting: NgForm}],
})
export class ProjectSetupComponent implements GlobalModalChild<ProjectModalData>, OnInit {
  @ViewChild('editProjectForm') editProjectForm;
  name: string;
  type: string;
  projectNumber?: string;
  projectCode: string;
  programName: string;
  programType: string;
  projectSegmentCode?: string;
  loading = false;

  editProject: ProjectModel;
  program: Program;
  selectedChartOfAccounts: ISubLedgerMap;
  globalModalParent: GlobalModalParent;
  minTTPFormGroup: FormGroup;
  ampProjectFormGroup: FormGroup;
  tracsMailboxNumberFormGroup: FormGroup;
  displayAmpProject = false;

  loggedInUser;

  generalInfoPanelOpenState = true;
  customAttributesInfoPanelOpenState = false;
  monthlyChargeInfoPanelOpenState = false;
  accountingInfoPanelOpenState = false;
  setAsideInfoPanelOpenState = false;

  @Input() programs: Program[];
  @Input() housingAuthority: Authority;
  @Input() chartOfAccounts?: ISubLedgerMap[];
  @Input() customAttributesTemplates: CustomAttributes[];
  // eslint-disable-next-line
  @Input() updateFunction: Function;
  @Input() editMode: boolean;
  @Input() selectedProject: ProjectModel;

  constructor(
    protected injector: Injector,
    public globalModalService: GlobalModalService,
    public housingServices: HousingServices,
    private formBuilder: FormBuilder,
    public projectService: ProjectsService
  ) {}

  ngOnInit(): void {
    this.editProject = lodash.cloneDeep(this.selectedProject);
    if (this.selectedProject.programId) {
      this.getProgram();
    }

    this.getSelectedChartOfAccounts();
    if (this.customAttributesTemplates.filter(attr => attr.required).length > 0) {
      this.customAttributesInfoPanelOpenState = true;
    }
    this.setMinTTPFormGroup();
    this.setAmpProjectFormGroup();
    this.setTracsMailboxNumberFormGroup();

    if (!this.editProject.taxCreditSetAsides) {
      this.editProject.taxCreditSetAsides = {
        projectId: this.editProject.id,
        siteId: this.editProject.siteId,
        unitsAt30: null,
        unitsAt40: null,
        unitsAt50: null,
        unitsAt60: null,
        unitsAtOther: null,
      };
    }
  }

  compareValues(o1, o2) {
    if (o1 == o2) {
      return true;
    } else {
      return false;
    }
  }

  getProgram() {
    this.program = this.programs.find(program => program.id == this.selectedProject.programId);
    this.selectedProject.programName = this.program.name;
    this.program.rentCalc.allowableDependentDeduction = this.program.rentCalc.allowableDependentDeduction / 100;
    this.program.rentCalc.allowableElderlyDisabilityDeduction = this.program.rentCalc.allowableElderlyDisabilityDeduction / 100;
    if (this.editProject.projectOverrides) {
      this.editProject.rentCalc = this.editProject.projectOverrides;
    } else {
      this.editProject.rentCalc = this.program.rentCalc as ProjectOverride;
    }
  }

  getSelectedChartOfAccounts() {
    if (this.selectedProject.chartOfAccounts) {
      this.selectedChartOfAccounts = this.chartOfAccounts.find(coa => coa._id == this.selectedProject.chartOfAccounts);
    }
  }

  onAmpProjectChange() {
    if (this.ampProjectFormGroup.valid) {
      this.onCheckPristineForm();
    }
  }

  onCheckPristineForm() {
    if (this.editProjectForm.valid) {
      this.editProjectForm.form.pristine = false;
    }
  }

  overrideChanged = event => {
    this.editProjectForm.form.pristine = false;
    this.editProject.rentCalc = event;
  };

  closeDialog = () => {
    this.globalModalParent.closePopup();
  };

  programSelected(event) {
    if (event.value !== null) {
      this.program = this.programs.find(program => program.name === event.value);
      this.selectedProject.programType = this.program.programType;
      this.editProject.programId = this.program.id;
      this.setMinTTPFormGroup();
      this.setAmpProjectFormGroup();
      this.setTracsMailboxNumberFormGroup();
    }
  }

  setTracsMailboxNumberFormGroup() {
    if (this.program?.programType === 'Multifamily') {
      this.editProject.projectNumber = '';
      const initialTracsMailboxNumber = this.selectedProject.tracsMailboxNumber ? this.selectedProject.tracsMailboxNumber : '';
      return (this.tracsMailboxNumberFormGroup = this.formBuilder.group({
        tracsMailboxNumber: [initialTracsMailboxNumber, [Validators.required]],
      }));
    }

    // reset tracsMailboxNumber for non-Multifamily program types
    this.editProject.tracsMailboxNumber = '';
  }

  setMinTTPFormGroup() {
    if (this.program && this.program.programType == 'Public Housing') {
      const initialMinTTP =
        this.selectedProject.rentCalc && this.selectedProject.rentCalc.minTTP ? this.selectedProject.rentCalc.minTTP : 0;
      return (this.minTTPFormGroup = this.formBuilder.group({
        minTTP: [initialMinTTP, [Validators.min(0), Validators.max(51), Validators.pattern(/^[^.]*$/), Validators.required]],
      }));
    }
  }

  setAmpProjectFormGroup() {
    if (this.program && (this.program.programType == 'Public Housing' || this.program.programType == 'Low Rent')) {
      this.displayAmpProject = true;
      const initialAmpProject = this.selectedProject.ampProject;
      return (this.ampProjectFormGroup = this.formBuilder.group({
        ampProject: [initialAmpProject, [Validators.minLength(6), Validators.maxLength(6), Validators.pattern(/(^[0-9]+$)/)]],
      }));
    }
  }

  getHeaderText() {
    return `${this.editMode ? this.editProject.name : 'New Project'} `;
  }

  getCurrentUsersLogInCookie() {
    const cookies = document.cookie.split(';');
    let usersAuthCookie;
    for (const cookie of cookies) {
      if (cookie.includes('LastAuthUser')) {
        //found the cookie of the user
        usersAuthCookie = cookie;
      }
    }
    const arrayOfCookieStrings = usersAuthCookie.split('=');
    this.loggedInUser = arrayOfCookieStrings[1];
  }

  saveAmpProject() {
    if (this.program.programType !== 'Public Housing' && this.program.programType !== 'Low Rent') {
      return;
    }
    const ampProject = this.ampProjectFormGroup.get('ampProject').value;
    this.editProject.ampProject = ampProject != '' ? ampProject : null;
  }

  onSubmit() {
    if (this.editProject.programId && this.editProject.rentCalc) {
      this.editProject.rentCalc.minTTP = this.program.type == 'Public Housing' ? this.minTTPFormGroup.get('minTTP').value : null;
    }
    if (this.editProject.rentCalc && this.editProject.rentCalc.id == this.program.rentCalc.id) {
      this.editProject.rentCalc = null;
    }
    this.editProject.chartOfAccounts = this.editProject.chartOfAccounts == '' ? null : this.editProject.chartOfAccounts;
    this.saveAmpProject();
    if (this.editProject.customAttributes.length > 0) {
      if (this.editProject.customAttributes[0].entityAttributeId != null) {
        const attributeValues: CustomAttributesValue[] = [];
        this.editProject.customAttributes.forEach((a): void => {
          const value = new CustomAttributesValue(a);
          value.entityInstanceId = this.editProject.id;
          try {
            attributeValues.push(value as CustomAttributesValue);
          } catch (error) {
            console.log(error);
          }
        });
        this.editProject.customAttributes = attributeValues;
      }
    }
    this.updateFunction(this.editProject);
    this.globalModalParent.closePopup();
  }
}
